import { CUSTOMIZE_WEBINAR_ROUTE } from '@anm/constants/routes';
import redirect from '@anm/helpers/redirect';
import uploadsReducer, { uploadsSaga } from '@anm/store/modules/uploads';
import userDestinationsReducer, { userDestinationsSaga } from '@anm/store/modules/userDestinations';
import { useCallback, useState, FC } from 'react';
import { useSelector } from 'react-redux';
import { compose } from 'recompose';
import { NextInitialProps } from 'types/anm-next';

import bootstrapPage from '../../HOCs/bootstrapPage';
import injectSagasAndReducers from '../../HOCs/injectSagasAndReducers';
import withAuthGuard from '../../HOCs/withAuthGuard';
import MetaHead from '../../components/MetaHead';
import WebinarSection from '../../components/webinar/WebinarSection';
import routes from '../../routes';
import analyticsReducer, { analyticsDynamicSaga } from '../../store/modules/analytics';
import brandsReducer, { brandsSaga } from '../../store/modules/brands';
import collapseReducer, { collapseSaga } from '../../store/modules/collapse';
import domainsReducer, { domainsSaga } from '../../store/modules/domains';
import hostedVideoReducer, { hostedVideoSaga } from '../../store/modules/hostedVideo';
import landingsReducer, { landingsSaga } from '../../store/modules/landings';
import streamReducer, { streamSaga } from '../../store/modules/stream';
import { userSelectors } from '../../store/modules/user';
import userPrefsReducer, { userPrefsSaga } from '../../store/modules/userprefs';
import videoFolderReducer, { videoFolderSaga } from '../../store/modules/videoFolder';

const { Router } = routes;

export type WebinarPageProps = {
  webinarId: string;
  currentTab: TabItem;
};

export enum WEBINAR_TABS {
  webinar,
  embed,
  access,
  email,
  domains
}

export type TabItem = {
  index: WEBINAR_TABS;
  title: string;
};

export const tabMap: { [key: string]: TabItem } = {
  webinar: {
    index: WEBINAR_TABS.webinar,
    title: 'Webinar page'
  },
  embed: {
    index: WEBINAR_TABS.embed,
    title: 'Embed'
  },
  access: {
    index: WEBINAR_TABS.access,
    title: 'Access'
  },
  'email-campaign': {
    index: WEBINAR_TABS.email,
    title: 'Email Campaign'
  },
  domains: {
    index: WEBINAR_TABS.domains,
    title: 'Custom domains'
  }
};

const WebinarPage = ({ currentTab, webinarId }: WebinarPageProps) => {
  const [activeTab, setActiveTab] = useState(currentTab.index);
  const handleTabChange = useCallback((tab: WEBINAR_TABS) => {
    const selectedTab = Object.keys(tabMap).find(o => tabMap[o].index === tab);

    Router.pushRoute(
      CUSTOMIZE_WEBINAR_ROUTE,
      { webinarId, ...(selectedTab && { tab: selectedTab }) },
      { shallow: true }
    );

    setActiveTab(tabMap[selectedTab!].index);
  }, []);

  const isWebinarEnabled = useSelector(userSelectors.selectIsWebinarsEnabled);

  if (!isWebinarEnabled) return null;

  return (
    <>
      <MetaHead title={`${currentTab.title} | Wave.video`} />
      <WebinarSection activeButton={activeTab} onActiveButtonChange={handleTabChange} {...{ webinarId }} />
    </>
  );
};

WebinarPage.getInitialProps = (ctx: NextInitialProps<{ webinarId: string; tab: string }>) => {
  const { tab = 'webinar', webinarId } = ctx.query;

  const isTabExist = tabMap.hasOwnProperty(tab);
  if (!isTabExist) redirect(ctx, `/_error`);

  const currentTab = tabMap[tab];
  return { currentTab, webinarId };
};

export default compose(
  bootstrapPage,
  withAuthGuard({ redirectTo: 'login' }),
  injectSagasAndReducers(
    () => [
      ...analyticsDynamicSaga(),
      ...hostedVideoSaga(),
      ...videoFolderSaga(),
      ...landingsSaga(),
      ...collapseSaga(),
      ...brandsSaga(),
      ...streamSaga(),
      ...userPrefsSaga(),
      ...uploadsSaga(),
      ...userDestinationsSaga(),
      ...domainsSaga()
    ],
    {
      brands: brandsReducer,
      collapse: collapseReducer,
      landings: landingsReducer,
      analytics: analyticsReducer,
      videoFolder: videoFolderReducer,
      hostedVideo: hostedVideoReducer,
      stream: streamReducer,
      uploads: uploadsReducer,
      userPrefs: userPrefsReducer,
      domains: domainsReducer,
      userDestinations: userDestinationsReducer
    },
    { id: 'WebinarPage' }
  )
)(WebinarPage as FC);
